import { FC } from 'react';

import { useFormik } from 'formik';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';

import { DomainForm } from './form';

import css from './AddDomainForm.module.css';

import { domainService } from 'v2/api/domainService';
import { Card } from 'v2/components/card/Card';
import { CardHeader } from 'v2/components/card/cardHeader/CardHeader';
import { handleHttpError } from 'v2/utils';

interface AddDomainFormProps {
  onSuccess: VoidFunction;
}

export const AddDomainForm: FC<AddDomainFormProps> = ({ onSuccess }) => {
  const { trigger, isMutating } = useSWRMutation('createDomain', domainService.createDomain, {
    onSuccess: () => {
      onSuccess();
      toast.success('The domain has been successfully created');
      formik.resetForm();
    },
    onError: handleHttpError,
  });

  const formik = useFormik<DomainForm>({
    initialValues: { domainName: '' },
    onSubmit: (values) => {
      trigger(values.domainName);
    },
  });

  return (
    <Card className={css.addDomainFormContainer}>
      <CardHeader title="Add New Domain" icon={Plus} />

      <form className="form" onSubmit={formik.handleSubmit}>
        <TextField
          name="domainName"
          value={formik.values.domainName}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          label="Domain"
          required
        />

        <Button type="submit" loading={isMutating} variant="contained" size="large">
          Create
        </Button>
      </form>

      <Divider />

      <Alert severity="info">
        <div className={css.addDomainFormTipsContainer}>
          <p className={css.addDomainFormTipsTitle}>Quick Tips</p>

          <ul className={css.addDomainFormTipsList}>
            <li>Use descriptive names for easy identification</li>
            <li>Ensure URLs include the protocol (https://)</li>
            <li>Test domains before activating</li>
          </ul>
        </div>
      </Alert>
    </Card>
  );
};
